import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { StarIcon } from "lucide-react";
import type { DefaultPersonaState } from "./container-members-dialog.types";

type DefaultPersonaToggleProps = {
  aiPersonaId: string;
  /** Persona display name — carried into the accessible name so rows differ. */
  personaName: string;
  state: DefaultPersonaState;
  onToggle: (aiPersonaId: string) => void;
};

/**
 * Per-persona marker for "answers when no one is @mentioned" (CR-46, CR-177).
 *
 * Two states, one glyph: the filled star marks the chat's EFFECTIVE default —
 * the stored persona while it participates, otherwise the Kairo/first
 * fallback — and every other assistant gets the outline star. There is always
 * exactly one filled star (CR-177 replaced the CR-46 half star, which read as
 * a clipped icon). The fill and `aria-pressed` carry the state, not shade
 * alone, and the tooltip says what the star means or what a click does.
 *
 * Clicking the default is a no-op handled by the hook (a chat always has one
 * default, so its star cannot be removed); the button still stays enabled so its
 * tooltip and accessible name remain reachable. Never `disabled` while a write
 * is in flight either: removing the button from the tab order mid-interaction
 * throws focus to <body>, and the optimistic cache patch already moves the
 * marker instantly. Double-fires are prevented by the hook's isPending guard.
 */
export function DefaultPersonaToggle({
  aiPersonaId,
  personaName,
  state,
  onToggle,
}: DefaultPersonaToggleProps) {
  const isDefault = state === "default";

  // Every message starts with literal text, never with the interpolation —
  // wuchale silently skips a string that opens with an expression.
  //
  // The tooltip is a contiguous substring of the accessible name (WCAG 2.5.3
  // Label in Name): the tooltip explains the mechanism, and the name adds which
  // persona this row is.
  const tooltip = isDefault
    ? "Default assistant — answers when no one is mentioned"
    : "Make this the default assistant";

  const label = isDefault
    ? `Default assistant — answers when no one is mentioned: ${personaName}`
    : `Make this the default assistant: ${personaName}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => onToggle(aiPersonaId)}
          aria-pressed={isDefault}
          aria-label={label}
          data-testid={`default-persona-toggle-${aiPersonaId}`}
          data-default-state={state}
          className={cn(
            // DESIGN_STANDARDS focus pattern: the Button baseline ring
            // (ring-ring/50) computes ~2:1, short of the 3:1 a focus indicator
            // needs, so this replaces it rather than adding to it.
            "focus-visible:ring-ring text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-offset-2",
            isDefault && "text-primary hover:text-primary",
          )}
        >
          <StarIcon className={cn("size-4", isDefault && "fill-current")} aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
